import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { FileDown, ArrowRight, ArrowLeft } from 'lucide-react';

const DigitalResourcesSection = () => {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/cms/digital-assets`);
        if (res.ok) {
          const data = await res.json();
          setAssets(data);
        }
      } catch (err) {
        console.error('Error fetching digital assets:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, [backendUrl]);

  if (loading || assets.length === 0) return null;

  const featured = assets[0];
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <section id="resources" className="section-padding bg-charcoal">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">

        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
            {isRTL ? 'موارد مجانية' : 'Free Resources'}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream leading-tight mb-4 sm:mb-6 px-2">
            {isRTL ? 'أدوات للشفاء والنمو' : 'Tools for Healing & Growth'}
          </h2>
          <div className="gold-divider" />
        </div>

        {/* Featured asset card */}
        <div className="max-w-2xl mx-auto">
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 p-6 sm:p-8 border border-gold/25 bg-charcoal-100 rounded-2xl overflow-hidden">

            {/* Top gold accent line */}
            <div className="absolute top-0 left-10 right-10 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

            {/* Icon */}
            <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gold/15 ring-1 ring-gold/35 flex items-center justify-center">
              <FileDown size={26} className="text-gold" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-gold/80 mb-1">
                {isRTL ? 'تنزيل مجاني' : 'Free Download'}
              </p>
              <h3 className="font-serif text-lg sm:text-xl text-cream mb-1.5 leading-snug">
                {featured.title}
              </h3>
              {featured.description && (
                <p className="text-cream-400 text-sm leading-relaxed">
                  {featured.description}
                </p>
              )}
            </div>

            {/* CTA */}
            <div className="w-full sm:w-auto flex-shrink-0">
              <button
                onClick={() => navigate(`/download/${featured.share_token}`)}
                className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-5 py-3 bg-gold text-charcoal text-xs uppercase tracking-wider font-semibold rounded-lg hover:bg-gold-500 active:scale-[0.98] transition-all"
              >
                <span>{isRTL ? 'تنزيل المورد' : 'Download Resource'}</span>
                <ArrowIcon size={15} className="text-charcoal flex-shrink-0" />
              </button>
            </div>
          </div>

          {/* Additional assets if more than one */}
          {assets.length > 1 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {assets.slice(1, 3).map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => navigate(`/download/${asset.share_token}`)}
                  className="flex items-center gap-3 p-4 border border-white/10 bg-charcoal-100 rounded-xl hover:border-gold/30 transition-all text-left group"
                >
                  <div className="w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                    <FileDown size={16} className="text-gold" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-cream text-sm font-medium truncate">{asset.title}</p>
                    <p className="text-gold/70 text-xs">{isRTL ? 'تنزيل مجاني' : 'Free download'}</p>
                  </div>
                  <ArrowIcon size={14} className="text-gold/50 group-hover:text-gold flex-shrink-0 ml-auto transition-colors" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DigitalResourcesSection;
